import type {
  AdminUserEmailFilter,
  AdminUserListItem,
  AdminUserRoleFilter,
  AdminUserStatusFilter,
} from './api-client';

export const ADMIN_USERS_FALLBACK_PER_PAGE = 20;
const FILTER_PARAM = 'admin_users_filter';
const SEARCH_PARAM = 'admin_users_q';
const PAGE_PARAM = 'admin_users_page';

export type AdminUsersFilterKey = 'all' | 'maintainers' | 'unverified' | 'suspended';

const FILTER_SUMMARY: Record<AdminUsersFilterKey, string> = {
  all: 'users',
  maintainers: 'maintainers',
  unverified: 'unverified users',
  suspended: 'suspended users',
};

export interface AdminUsersViewState {
  filter: AdminUsersFilterKey;
  search: string;
  page: number;
}

export interface AdminUsersLoadedPageInput {
  requestedPage: number;
  total: number;
  perPage?: number;
  totalPages?: number;
}

export interface AdminUserActionSummary {
  label: string;
  detail: string;
  tone: 'red' | 'amber' | 'blue' | 'brand' | 'zinc';
}

function normalizeFilter(value: string | null): AdminUsersFilterKey {
  return value === 'maintainers' || value === 'unverified' || value === 'suspended' ? value : 'all';
}

function normalizePage(value: string | null): number {
  const parsed = Number.parseInt(String(value || ''), 10);
  return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
}

export function resolveAdminUsersViewState(search: string): AdminUsersViewState {
  const params = new URLSearchParams(search);
  return {
    filter: normalizeFilter(params.get(FILTER_PARAM)),
    search: String(params.get(SEARCH_PARAM) || '').trim(),
    page: normalizePage(params.get(PAGE_PARAM)),
  };
}

export function buildAdminUsersViewSearch(search: string, state: AdminUsersViewState): string {
  const params = new URLSearchParams(search);
  const filter = normalizeFilter(state.filter);
  const query = state.search.trim();
  const page = Math.max(1, Math.floor(state.page || 1));
  filter === 'all' ? params.delete(FILTER_PARAM) : params.set(FILTER_PARAM, filter);
  query ? params.set(SEARCH_PARAM, query) : params.delete(SEARCH_PARAM);
  page > 1 ? params.set(PAGE_PARAM, String(page)) : params.delete(PAGE_PARAM);
  const next = params.toString();
  return next ? `?${next}` : '';
}

export function hasAdminUsersViewState(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has(FILTER_PARAM) || params.has(SEARCH_PARAM) || params.has(PAGE_PARAM);
}

function describeView(prefix: 'Showing' | 'No', state: AdminUsersViewState): string {
  const filter = normalizeFilter(state.filter);
  const query = state.search.trim();
  const page = Math.max(1, Math.floor(state.page || 1));
  const pieces = [`${prefix} ${FILTER_SUMMARY[filter]}`];
  if (query) pieces.push(`matching "${query}"`);
  if (page > 1) pieces.push(`on page ${page}`);
  return pieces.join(' ') + '.';
}

export function buildAdminUsersViewSummary(state: AdminUsersViewState): string {
  if (normalizeFilter(state.filter) === 'all' && !state.search.trim() && state.page <= 1) return '';
  return describeView('Showing', state);
}

export function buildAdminUsersEmptyMessage(state: AdminUsersViewState): string {
  if (normalizeFilter(state.filter) === 'all' && !state.search.trim() && state.page <= 1) return 'No users found';
  return describeView('No', state);
}

export function resolveAdminUsersLoadedPage(input: AdminUsersLoadedPageInput): number {
  const requested = Math.max(1, Math.floor(input.requestedPage || 1));
  const perPage = input.perPage && input.perPage > 0 ? Math.floor(input.perPage) : ADMIN_USERS_FALLBACK_PER_PAGE;
  const totalPages = input.totalPages && input.totalPages > 0
    ? Math.floor(input.totalPages)
    : Math.ceil(Math.max(0, Math.floor(input.total || 0)) / perPage);
  return Math.min(requested, Math.max(1, totalPages));
}

export function buildAdminUserActionSummary(user: Pick<AdminUserListItem, 'role' | 'email_verified_at' | 'active_access_token_count' | 'suspended_at'>): AdminUserActionSummary {
  if (user.suspended_at) return { label: 'Suspended', detail: 'Cannot sign in until reactivated.', tone: 'red' };
  if (!user.email_verified_at) return { label: 'Needs email verification', detail: 'Cannot create agent tokens yet.', tone: 'amber' };
  if (Number(user.active_access_token_count || 0) <= 0) {
    return { label: 'No active tokens', detail: 'Ready for account setup, but no agent token is active.', tone: 'blue' };
  }
  if (user.role === 'maintainer') {
    return { label: 'Maintainer ready', detail: 'Can review submissions and use maintainer tools.', tone: 'brand' };
  }
  return { label: 'Account ready', detail: 'Verified account with active agent access.', tone: 'zinc' };
}

export function adminUserFilterParams(filter: AdminUsersFilterKey): {
  role?: AdminUserRoleFilter;
  status?: AdminUserStatusFilter;
  email?: AdminUserEmailFilter;
} {
  if (filter === 'maintainers') return { role: 'maintainer' };
  if (filter === 'unverified') return { email: 'unverified' };
  if (filter === 'suspended') return { status: 'suspended' };
  return {};
}
